import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Badge from "../badges/badge";
import LiveBadge from "../badges/livebadge";
import dayjs from "dayjs";

const MatchCard = ({ matches }) => {
  const navigate = useNavigate();

  const handleMatchClick = (match) => {
    // Only allow clicks for football matches (not NBA or NCAA)
    if (match.league === "NBA" || match.league === "NCAA") {
      return; // Do nothing for basketball matches
    }
    console.log(match)
    navigate(`/match-details?match=${match.fixtureId}`);
  };

  return (
    <div className="space-y-4 p-2">
      {matches.map((match, index) => {
        const [hour, mins] = match.time.split(":");
        const matchTime = dayjs(match.time);
        const matchEndTime = matchTime.add(2, "hours");
        const currentTime = dayjs();
        const isLive = matchTime <= currentTime && matchEndTime >= currentTime;
        const isBasketball = match.league === "NBA" || match.league === "NCAA";

        return (
          <div 
            key={index} 
            className={`bg-white rounded-xl shadow-sm p-6 mb-4 max-w-none w-full transition-shadow duration-200 ${
              isBasketball 
                ? "cursor-not-allowed opacity-75" 
                : "cursor-pointer hover:shadow-md"
            }`}
            onClick={() => handleMatchClick(match)}
          >
            {/* League Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <img
                  src={match.countryFlag}
                  alt={match.league}
                  className="w-10 h-10 rounded-full mr-3"
                />
                <h3 className="text-sm font-medium text-black">
                  {match.league}
                </h3>
              </div>
              {match.played && (
                <Badge 
                  status={`${(match.league === "NBA" || match.league === "NCAA") 
                    ? (match.wonResultPrediction || match.wonOverUnderPrediction ? "won" : "lost")
                    : (match.wonResultPrediction || match.wonGGNGPrediction || match.wonOverUnderPrediction ? "won" : "lost")}`} 
                />
              )}
            </div>

            {/* Teams Section */}
            <div className="flex items-center mb-6">
              {/* Stacked Time */}
              <div className="flex flex-col items-center justify-center mr-4">
                <span className="text-gray-400 text-xs font-medium">{hour}</span>
                <span className="text-gray-400 text-xs font-medium">{mins}</span>
              </div>

              {/* Vertical Divider */}
              <div className="w-px bg-gray-300 h-12 mr-4"></div>

              {/* Teams */}
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <img
                    src={match.team1Emblem}
                    alt={match.team1}
                    className="w-8 h-8 rounded-full mr-4"
                  />
                  <span className="text-black font-normal text-sm">
                    {match.team1}
                  </span>
                </div>
                <div className="flex items-center">
                  <img
                    src={match.team2Emblem}
                    alt={match.team2}
                    className="w-8 h-8 rounded-full mr-4"
                  />
                  <span className="text-black font-normal text-sm">
                    {match.team2}
                  </span>
                </div>
              </div>
            </div>

            {/* Live Badge */}
            {isLive && (
              <div className="mb-4">
                <LiveBadge />
              </div>
            )}

            {/* Predictions Section */}
            <div className="mt-6">
              <p style={{ color: '#2C0053' }} className="font-normal text-sm mb-4">
                Prediction:
              </p>
              <div className="bg-gray-50 rounded-xl py-4 px-6">
                <div className="flex flex-wrap gap-6">
                  {match.league === "NBA" || match.league === "NCAA" ? (
                    <>
                      <div className="flex flex-col">
                        <span className="text-gray-400 text-xs font-medium mb-1">Result:</span>
                        <span style={{ color: '#2C0053' }} className="text-xs font-medium">
                          {match.resultPrediction === "away" ? "Away Win" : 
                           match.resultPrediction === "home" ? "Home Win" : "NED"}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-gray-400 text-xs font-medium mb-1">O/U:</span>
                        <span style={{ color: '#2C0053' }} className="text-xs font-medium">
                          {match.overUnderPrediction === "Over" ? `Over ${match.league === "NBA" ? "200.5" : "150.5"}` : 
                           match.overUnderPrediction === "Under" ? `Under ${match.league === "NBA" ? "200.5" : "150.5"}` : "NED"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col">
                        <span className="text-gray-400 text-xs font-medium mb-1">1X2/DC:</span>
                        <span style={{ color: '#2C0053' }} className="text-xs font-medium">
                          {match.resultPrediction === "1" ? "Home Win" : 
                           match.resultPrediction === "X" ? "Draw" : 
                           match.resultPrediction === "2" ? "Away Win" : 
                           match.resultPrediction === "1X" ? "Home Win or Draw" : 
                           match.resultPrediction === "X2" ? "Draw or Away Win" : 
                           match.resultPrediction === "12" ? "Home Win or Away Win" : "NED"}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-gray-400 text-xs font-medium mb-1">GG/NG:</span>
                        <span style={{ color: '#2C0053' }} className="text-xs font-medium">
                          {match.ggngPrediction === "1" ? "GG" : 
                           match.ggngPrediction === "0" ? "NG" : "NED"}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-gray-400 text-xs font-medium mb-1">O/U:</span>
                        <span style={{ color: '#2C0053' }} className="text-xs font-medium">
                          {match.overUnderPrediction === "Over" ? "Over 2.5" : 
                           match.overUnderPrediction === "Under" ? "Under 2.5" : "NED"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Results Section */}
              {match.played && (
                <div className="mt-4">
                  <p style={{ color: '#2C0053' }} className="font-normal text-sm mb-3">Result:</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {match.league === "NBA" || match.league === "NCAA" ? (
                      <span style={{ color: '#2C0053' }} className="font-medium text-xs">
                        {match.wonResultPrediction && match.resultPrediction === "home" ? "Home Win" : 
                         match.wonResultPrediction && match.resultPrediction === "away" ? "Away Win" : null}
                        {match.wonResultPrediction && match.wonOverUnderPrediction ? ", " : ""}
                        {match.wonOverUnderPrediction && match.overUnderPrediction === "Over" ? `Over ${match.league === "NBA" ? "200.5" : "150.5"}` : 
                         match.wonOverUnderPrediction && match.overUnderPrediction === "Under" ? `Under ${match.league === "NBA" ? "200.5" : "150.5"}` : null}
                      </span>
                    ) : (
                      <span style={{ color: '#2C0053' }} className="font-medium text-xs">
                        {match.wonResultPrediction && match.resultPrediction === "1" ? "Home Win" : 
                         match.wonResultPrediction && match.resultPrediction === "X" ? "Draw" : 
                         match.wonResultPrediction && match.resultPrediction === "2" ? "Away Win" : 
                         match.wonResultPrediction && match.resultPrediction === "1X" ? "Home Win or Draw" : 
                         match.wonResultPrediction && match.resultPrediction === "X2" ? "Draw or Away Win" : 
                         match.wonResultPrediction && match.resultPrediction === "12" ? "Home Win or Away Win" : null}
                        {match.wonResultPrediction && (match.wonGGNGPrediction || match.wonOverUnderPrediction) ? ", " : ""}
                        {match.wonGGNGPrediction && match.ggngPrediction === "1" ? "GG" : 
                         match.wonGGNGPrediction && match.ggngPrediction === "0" ? "NG" : null}
                        {match.wonGGNGPrediction && match.wonOverUnderPrediction ? ", " : ""}
                        {match.wonOverUnderPrediction && match.overUnderPrediction === "Over" ? "Over 2.5" : 
                         match.wonOverUnderPrediction && match.overUnderPrediction === "Under" ? "Under 2.5" : null}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

MatchCard.propTypes = {
  matches: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      league: PropTypes.string.isRequired,
      countryFlag: PropTypes.string.isRequired,
      team1: PropTypes.string.isRequired,
      team1Logo: PropTypes.string.isRequired,
      team2: PropTypes.string.isRequired,
      team2Logo: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default MatchCard;